import { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { Loader2, Save, RotateCcw, Undo2, Link2, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from '@/components/ui/dialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const clone = (v) => (typeof structuredClone === 'function' ? structuredClone(v) : JSON.parse(JSON.stringify(v)));

/**
 * AdminEventDayManager
 *
 * Edits the "Wedding Day Details" section of the home page. The three
 * cards (Ceremony, Cocktail Hour, Reception) are the same records as the
 * matching rows in "Order of Events" — editing them here mutates the same
 * schedule entries so both pages stay in lockstep.
 */
const AdminEventDayManager = ({ authHeader }) => {
  const [initial, setInitial] = useState(null);   // ancillary content only (venue/heading/dress/order)
  const [content, setContent] = useState(null);   // ancillary + cards (edited together)
  const [initialCards, setInitialCards] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedFlash, setSavedFlash] = useState(false);
  const [error, setError] = useState('');
  const [resetOpen, setResetOpen] = useState(false);
  const [resetting, setResetting] = useState(false);

  const stripCards = (doc) => {
    const { cards, ...rest } = doc || {};
    return rest;
  };

  const load = useCallback(async () => {
    setLoading(true); setError('');
    try {
      const res = await axios.get(`${API}/event-day/content`);
      const data = res.data || {};
      setInitial(clone(stripCards(data)));
      setInitialCards(clone(data.cards || []));
      setContent(clone(data));
    } catch { setError('Could not load current content.'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = (fn) => setContent((prev) => { const n = clone(prev); fn(n); return n; });

  const ancillaryDirty = useMemo(() => (
    content && initial && JSON.stringify(stripCards(content)) !== JSON.stringify(initial)
  ), [content, initial]);
  const cardsDirty = useMemo(() => (
    content && initialCards && JSON.stringify(content.cards || []) !== JSON.stringify(initialCards)
  ), [content, initialCards]);
  const dirty = ancillaryDirty || cardsDirty;

  const save = async () => {
    if (!dirty) return;
    setSaving(true); setError('');
    try {
      // 1. Update ancillary content (heading, venue, dress code, ordering, bg)
      if (ancillaryDirty) {
        await axios.put(`${API}/admin/event-day/content`, stripCards(content), { headers: authHeader() });
      }
      // 2. For each changed card, PUT the shared schedule item by key.
      //    This is the same record edited from the Order of Events page.
      if (cardsDirty && Array.isArray(content.cards)) {
        for (const card of content.cards) {
          const before = (initialCards || []).find((x) => x.key === card.key);
          if (!before) continue;
          if (before.title === card.title && before.time === card.time && before.description === card.description) continue;
          await axios.put(`${API}/admin/schedule/items/${card.key}`, {
            event: card.title,
            time: card.time,
            description: card.description,
          }, { headers: authHeader() });
        }
      }
      // 3. Reload from the merged endpoint so the next save baseline is correct
      const res = await axios.get(`${API}/event-day/content`);
      const data = res.data || {};
      setInitial(clone(stripCards(data)));
      setInitialCards(clone(data.cards || []));
      setContent(clone(data));
      setSavedFlash(true);
      setTimeout(() => setSavedFlash(false), 2200);
    } catch (e) {
      setError(e?.response?.data?.detail || 'Failed to save.');
    }
    finally { setSaving(false); }
  };

  const resetDefaults = async () => {
    setResetting(true);
    try {
      await axios.post(`${API}/admin/event-day/content/reset`, {}, { headers: authHeader() });
      await load();
      setResetOpen(false);
    } catch { setError('Failed to reset content.'); }
    finally { setResetting(false); }
  };

  const revert = () => {
    setContent({ ...clone(initial), cards: clone(initialCards || []) });
  };

  if (loading || !content) {
    return <div className="bg-white rounded-xl p-10 flex items-center justify-center text-[#5A4A4A] gap-2">
      <Loader2 className="w-4 h-4 animate-spin" /> Loading Wedding Day Details…
    </div>;
  }

  const cards = content.cards || [];
  const venue = content.venue || {};
  const dressCode = content.dressCode || {};

  const setCardField = (i, key, val) => patch((d) => { d.cards[i][key] = val; });
  const moveCard = (i, dir) => patch((d) => {
    const j = i + dir;
    if (j < 0 || j >= d.cards.length) return;
    [d.cards[i], d.cards[j]] = [d.cards[j], d.cards[i]];
    // Keep cardOrder in sync so the public page renders in the same order
    d.cardOrder = d.cards.map((c) => c.key);
  });

  return (
    <div className="space-y-4" data-testid="admin-event-day">
      <div className="bg-white rounded-xl shadow-sm p-4 sticky top-2 z-10 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-serif text-xl text-[#3B2F2F]">Wedding Day Details</h2>
          <p className="text-xs text-[#8A7A7A]">
            Edits appear on the home page. Ceremony, Cocktail Hour, and Reception are shared
            with <span className="underline">Order of Events</span> — editing either surface updates the same record.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setResetOpen(true)} className="text-xs text-[#8A7A7A] hover:text-red-500 inline-flex items-center gap-1" data-testid="event-day-reset-btn">
            <RotateCcw className="w-3 h-3" /> Reset defaults
          </button>
          {dirty && (
            <button onClick={revert} disabled={saving}
              className="inline-flex items-center gap-1 px-3 py-1.5 rounded-full text-xs bg-[#F8F5EF] text-[#3B2F2F] hover:bg-[#E5E0D8]"
              data-testid="event-day-revert-btn">
              <Undo2 className="w-3.5 h-3.5" /> Discard
            </button>
          )}
          <Button onClick={save} disabled={!dirty || saving} className="bg-[#C8A951] hover:bg-[#B09240] text-white disabled:opacity-60" data-testid="event-day-save-btn">
            {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
            {savedFlash ? 'Saved ✓' : dirty ? 'Save changes' : 'Saved'}
          </Button>
        </div>
      </div>
      {error && <p className="text-red-500 text-xs">{error}</p>}

      {/* Heading */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Eyebrow (script line)</label>
            <Input value={content.eyebrow || ''} onChange={(e) => patch((d) => { d.eyebrow = e.target.value; })} data-testid="event-day-eyebrow" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
            <Input value={content.title || ''} onChange={(e) => patch((d) => { d.title = e.target.value; })} data-testid="event-day-title" />
          </div>
        </div>
      </div>

      {/* Venue */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <h3 className="font-serif text-xl text-[#3B2F2F]">Venue</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Section label</label>
            <Input value={content.venueLabel || ''} onChange={(e) => patch((d) => { d.venueLabel = e.target.value; })} data-testid="event-day-venue-label" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Venue name</label>
            <Input value={venue.name || ''} onChange={(e) => patch((d) => { d.venue = { ...(d.venue || {}), name: e.target.value }; })} data-testid="event-day-venue-name" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Address</label>
            <Input value={venue.address || ''} onChange={(e) => patch((d) => { d.venue = { ...(d.venue || {}), address: e.target.value }; })} data-testid="event-day-venue-address" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Google Maps URL</label>
            <Input value={venue.mapUrl || ''} onChange={(e) => patch((d) => { d.venue = { ...(d.venue || {}), mapUrl: e.target.value }; })} data-testid="event-day-venue-map-url" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Map button label</label>
            <Input value={venue.mapButtonLabel || ''} onChange={(e) => patch((d) => { d.venue = { ...(d.venue || {}), mapButtonLabel: e.target.value }; })} data-testid="event-day-venue-map-btn" />
          </div>
        </div>
      </div>

      {/* Cards — shared with Order of Events */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h3 className="font-serif text-xl text-[#3B2F2F]">Cards</h3>
            <p className="text-xs text-[#8A7A7A]">
              Each card is the same record as the matching row in Order of Events.
              Editing the title, time, or description here updates both places.
            </p>
          </div>
        </div>

        <ul className="space-y-4">
          {cards.map((card, i) => (
            <li key={card.key} className="p-4 rounded-lg border border-[#E5E0D8] bg-[#FCFBF7]" data-testid={`event-day-card-row-${card.key}`}>
              <div className="flex items-center justify-between mb-3">
                <span className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-[#8A7A7A]">
                  <Link2 className="w-3 h-3 text-[#C8A951]" />
                  Synced with Order of Events • {card.key}
                </span>
                <div className="flex items-center gap-1">
                  <button onClick={() => moveCard(i, -1)} disabled={i === 0}
                    className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                    data-testid={`event-day-card-up-${card.key}`} title="Move up">
                    <ArrowUp className="w-4 h-4" />
                  </button>
                  <button onClick={() => moveCard(i, 1)} disabled={i === cards.length - 1}
                    className="p-1.5 rounded-md text-[#3B2F2F] hover:bg-[#F8F5EF] disabled:opacity-30"
                    data-testid={`event-day-card-down-${card.key}`} title="Move down">
                    <ArrowDown className="w-4 h-4" />
                  </button>
                </div>
              </div>
              <div className="grid md:grid-cols-[1fr_180px] gap-3">
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Title</label>
                  <Input value={card.title || ''} onChange={(e) => setCardField(i, 'title', e.target.value)} data-testid={`event-day-card-title-${card.key}`} />
                </div>
                <div>
                  <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Time</label>
                  <Input value={card.time || ''} onChange={(e) => setCardField(i, 'time', e.target.value)} data-testid={`event-day-card-time-${card.key}`} />
                </div>
              </div>
              <div className="mt-3">
                <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Description</label>
                <Textarea rows={2} value={card.description || ''} onChange={(e) => setCardField(i, 'description', e.target.value)} data-testid={`event-day-card-desc-${card.key}`} />
              </div>
            </li>
          ))}
        </ul>
      </div>

      {/* Dress Code */}
      <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 space-y-4">
        <h3 className="font-serif text-xl text-[#3B2F2F]">Dress Code</h3>
        <div className="grid md:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Label</label>
            <Input value={dressCode.label || ''} onChange={(e) => patch((d) => { d.dressCode = { ...(d.dressCode || {}), label: e.target.value }; })} data-testid="event-day-dress-label" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Value</label>
            <Input value={dressCode.value || ''} onChange={(e) => patch((d) => { d.dressCode = { ...(d.dressCode || {}), value: e.target.value }; })} data-testid="event-day-dress-value" />
          </div>
          <div className="md:col-span-2">
            <label className="text-xs uppercase tracking-widest text-[#8A7A7A] mb-1 block">Note</label>
            <Input value={dressCode.note || ''} onChange={(e) => patch((d) => { d.dressCode = { ...(d.dressCode || {}), note: e.target.value }; })} data-testid="event-day-dress-note" />
          </div>
        </div>
      </div>

      <Dialog open={resetOpen} onOpenChange={setResetOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reset Wedding Day Details to defaults?</DialogTitle>
            <DialogDescription>
              This restores the heading, venue, dress code, and card ordering.
              Card time & description come from Order of Events and are not reset here.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setResetOpen(false)} disabled={resetting}>Cancel</Button>
            <Button variant="destructive" onClick={resetDefaults} disabled={resetting} data-testid="event-day-reset-confirm">
              {resetting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <RotateCcw className="w-4 h-4 mr-1" />}Reset
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminEventDayManager;
